import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, Search, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";

const LOGO_WHITE =
  "/assets/fekkib9r_LOGO-UNIK-HOLDINGS-White-gold.png";

const buildNav = (t) => [
  { key: "home", label: t("nav.home"), to: "/" },
  {
    key: "about",
    label: t("nav.about"),
    to: "/about/who-we-are",
    match: "/about",
    children: [
      { key: "whoWeAre", label: t("nav.aboutMenu.whoWeAre"), to: "/about/who-we-are" },
      { key: "portfolioCompanies", label: t("nav.aboutMenu.portfolioCompanies"), to: "/about/portfolio-companies" },
      { key: "leadership", label: t("nav.aboutMenu.leadership"), to: "/about/leadership" },
    ],
  },
  {
    key: "investment",
    label: t("nav.investment"),
    to: "/investments/approach",
    match: "/investments",
    children: [
      { key: "approach", label: t("nav.investmentMenu.approach"), to: "/investments/approach" },
      { key: "portfolioAssets", label: t("nav.investmentMenu.portfolioAssets"), to: "/investments/portfolio-assets" },
    ],
  },
  { key: "impact", label: t("nav.impact"), to: "/impact" },
  { key: "insights", label: t("nav.insights"), to: "/insights" },
  { key: "careers", label: t("nav.aboutMenu.careers"), to: "/about/careers" },
  { key: "contact", label: t("nav.contact"), to: "/contact" },
];

export default function SiteNav() {
  const { t } = useTranslation();
  const NAV = buildNav(t);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const closeTimerRef = React.useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const submitSearch = (e) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchQuery("");
    setMobileOpen(false);
  };

  const handleEnter = (key) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setOpenMenu(key);
  };

  const handleLeave = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setOpenMenu(null);
      closeTimerRef.current = null;
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const isActiveTopLevel = (item) => {
    if (item.to === "/") return location.pathname === "/";
    const matchPath = item.match || item.to;
    return location.pathname.startsWith(matchPath);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#14355a]/95 backdrop-blur-md border-b border-white/10 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between gap-8">
        <Link
          to="/"
          className="flex items-center flex-shrink-0"
          data-testid="nav-logo"
        >
          <img
            src={LOGO_WHITE}
            alt="UNIK Holdings"
            className={`transition-all duration-500 ${
              scrolled ? "h-10" : "h-14"
            } w-auto`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-6 2xl:gap-8 ml-auto">
          {NAV.map((n) =>
            n.children ? (
              <div
                key={n.key}
                className="relative"
                onMouseEnter={() => handleEnter(n.key)}
                onMouseLeave={handleLeave}
              >
                <button
                  type="button"
                  data-testid={`nav-link-${n.key}`}
                  className={`font-display text-[13px] xl:text-[14px] font-medium tracking-wide whitespace-nowrap transition-colors duration-300 inline-flex items-center gap-1 ${
                    isActiveTopLevel(n)
                      ? "text-unik-gold"
                      : "text-white/90 hover:text-unik-gold"
                  }`}
                  onClick={() =>
                    setOpenMenu(openMenu === n.key ? null : n.key)
                  }
                  aria-expanded={openMenu === n.key}
                >
                  {n.label}
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-300 ${
                      openMenu === n.key ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {/* Dropdown panel — uses padding-top instead of margin-top so the hover bridge between button and panel is continuous (mouse won't leave the hover area while moving down) */}
                <div
                  className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 w-60 transition-all duration-300 ${
                    openMenu === n.key
                      ? "opacity-100 visible translate-y-0"
                      : "opacity-0 invisible -translate-y-2 pointer-events-none"
                  }`}
                  data-testid={`nav-dropdown-${n.key}`}
                  onMouseEnter={() => handleEnter(n.key)}
                  onMouseLeave={handleLeave}
                >
                  <div className="bg-[#14355a] border border-white/15 shadow-2xl py-2">
                    {n.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        end={c.to === n.to}
                        data-testid={`nav-dropdown-item-${c.key}`}
                        className={({ isActive }) =>
                          `block px-5 py-3 font-display text-[14px] tracking-wide transition-colors duration-200 ${
                            isActive
                              ? "text-unik-gold bg-white/[0.04]"
                              : "text-white/85 hover:text-unik-gold hover:bg-white/[0.04]"
                          }`
                        }
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={n.key}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.key}`}
                className={({ isActive }) =>
                  `font-display text-[13px] xl:text-[14px] font-medium tracking-wide whitespace-nowrap transition-colors duration-300 ${
                    isActive
                      ? "text-unik-gold"
                      : "text-white/90 hover:text-unik-gold"
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-3" data-testid="nav-search">
          <form onSubmit={submitSearch} className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("common.search")}
              data-testid="nav-search-input"
              className="bg-transparent border border-white/40 text-white placeholder-white/60 rounded-full pl-4 pr-10 py-2 text-[13px] font-display focus:outline-none focus:border-unik-gold transition-colors w-40 2xl:w-48"
            />
            <button
              type="submit"
              data-testid="nav-search-submit"
              aria-label={t("common.search")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-unik-gold transition-colors"
            >
              <Search size={14} />
            </button>
          </form>
          <LanguageSwitcher variant="dark" />
        </div>

        {/* Language switcher for lg (below xl, where the search field is hidden) */}
        <div className="hidden lg:flex xl:hidden items-center">
          <LanguageSwitcher variant="dark" />
        </div>

        {/* Mobile: language dropdown to the LEFT of hamburger */}
        <div className="lg:hidden flex items-center gap-3 ml-auto">
          <LanguageSwitcher variant="dark" align="right" />
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-white"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-[#0e2540] border-t border-white/10 max-h-[80vh] overflow-y-auto"
        >
          <div className="px-6 py-6 flex flex-col gap-2">
            {NAV.map((n) =>
              n.children ? (
                <div key={n.key}>
                  <button
                    type="button"
                    onClick={() =>
                      setMobileSubmenuOpen(
                        mobileSubmenuOpen === n.key ? null : n.key
                      )
                    }
                    className="w-full flex items-center justify-between py-3 font-display text-white/90 text-lg"
                  >
                    {n.label}
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${
                        mobileSubmenuOpen === n.key ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {mobileSubmenuOpen === n.key && (
                    <div className="pl-4 pb-2 flex flex-col gap-3 border-l border-white/15 ml-2">
                      {n.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => setMobileOpen(false)}
                          className="font-display text-white/75 text-base hover:text-unik-gold transition-colors py-1"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={n.key}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 font-display text-white/90 text-lg hover:text-unik-gold transition-colors"
                >
                  {n.label}
                </Link>
              )
            )}
            <Link
              to="/contact"
              onClick={() => setMobileOpen(false)}
              className="font-display inline-flex items-center gap-2 text-unik-gold mt-2 py-3"
            >
              {t("common.contactUs")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
